import React from 'react';
import styled, {ThemeContext} from 'styled-components';
import {
  Breakpoints as VwBreakpoints,
  ThemeDefinition,
  ThemeProvider,
} from '@volkswagen-onehub/components-core';
import {
  EMobilityToolsFeatureAppTheme,
  Breakpoints,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';

interface BrandThemeProviderProps {
  theme: 'main' | 'inverted';
  children: React.ReactNode;
}

export const BrandThemeProvider = (props: BrandThemeProviderProps) => {
  return (
    <ThemeProvider theme={props.theme}>
      <ThemeContext.Consumer>
        {(vwTheme) => (
          <ThemeProvider theme={getThemeWithoutDeprecatedObjects(vwTheme)}>
            {props.children}
          </ThemeProvider>
        )}
      </ThemeContext.Consumer>
    </ThemeProvider>
  );
};

export const EMobilityFeatureAppWrapper = (props: {
  children?: React.ReactNode;
}): JSX.Element => <BreakpointsWrapper>{props.children}</BreakpointsWrapper>;

// don't import/spread the whole theme, since it holds deprecated keys, that shouldn't be used anymore
export const getThemeWithoutDeprecatedObjects = (theme: any) => {
  return {
    animation: theme.animation,
    size: theme.size,
    fonts: theme.fonts,
    appearances: theme.appearances,
    colors: theme.colors,
  };
};

export function createEMobilityToolsFeatureAppTheme(
  theme: ThemeDefinition
): EMobilityToolsFeatureAppTheme {
  return {
    colors: {
      inputArea: {
        border: {
          color: theme.colors.border.default,
        },
        drivingProfileSlider: {
          evenBackground: 'transparent',
          oddBackground: theme.colors.background.tertiary,
          separator: theme.colors.border.default,
          text: theme.colors.content.active,
          thumb: {
            border: theme.colors.controllers.off.default,
            background: theme.colors.controllers.off.default,
            text: theme.colors.content.onsurface,
            focusBackground: theme.colors.controllers.on.default,
            focusText: theme.colors.content.onsurface,
          },
        },
      },
      resultArea: {
        background: theme.colors.gradient.right,
        borderColor: theme.colors.border.default,
      },
    },
    elements: {
      modelSelectionArea: {
        header: {
          marginTop: '35px', // this is because the corresponding headline's wrapper in the input section is higher due to the information button's height
        },
      },
      inputArea: {
        genericChoiceButton: {
          backgroundColor: {
            selected: 'transparent',
          },
          color: {
            default: theme.colors.button.secondary.default,
            selected: theme.colors.button.secondary.active,
            hover: theme.colors.button.secondary.hover,
            focus: theme.colors.focus.main,
            disabled: theme.colors.button.secondary.disabled,
          },
          border: {
            radius: {
              default: theme.size.static150,
              focus: theme.size.static150,
            },
            width: {
              default: '1px',
              selected: '2px',
            },
          },
          padding: {
            mobile: {
              withIcon: `7px ${theme.size.static200}`,
              withoutIcon: `${theme.size.static150} ${theme.size.static250}`,
            },
            desktop: {
              withIcon: theme.size.static250,
              withoutIcon: `${theme.size.static150} ${theme.size.static200}`,
            },
          },
        },
      },
      disclaimersArea: {
        marginTop: theme.size.dynamic0150,
      },
      efficiencyDataSeparator: '\u00a0• ',
      borderRadius: '8px',
    },
    spacing: {
      appLayout: {
        blockGap: theme.size.grid001,
      },
      simulatorHeaderLayout: {
        inlineStart: 'var(--header-layout-inline-start)',
        inlineEnd: 'var(--header-layout-inline-end)',
      },
      oneColumnSimulatorContentLayout: {
        inlineStart: '0',
        inlineEnd: '0',
        blockGap: theme.size.static300,
      },
      simulatorLayout: {
        blockGap: theme.size.dynamic0150,
      },
      twoColumnSimulatorContentLayout: {
        inlineStart: 'var(--two-column-simulator-content-layout-inline-start)',
        inlineEnd: 'var(--two-column-simulator-content-layout-inline-end)',
        inlineGap: theme.size.grid001,
        blockGap: theme.size.grid001,
        columnWidths: ['2fr', '3fr'],
      },
      carSelectionSectionLayout: {
        inlineStart: 'var(--carselection-inline-start)',
        inlineEnd: 'var(--carselection-inline-end)',
        blockStart: '0',
        blockEnd: '0',
      },
      outputSectionRangeLayout: {
        inlineStart: 'var(--output-range-inline-start)',
        inlineEnd: 'var(--output-range-inline-end)',
      },
      outputSectionChargeCostLayout: {
        inlineStart: 'var(--output-charge-cost-inline-start)',
        inlineEnd: 'var(--output-charge-cost-inline-end)',
      },
      inputSectionLayout: {
        inlineStart: theme.size.static520,
        inlineEnd: theme.size.static520,
        blockStart: theme.size.static400,
        blockEnd: theme.size.static400,
      },
      faqLayout: {
        inlineStart: 'var(--faq-layout-inline-start)',
        inlineEnd: 'var(--faq-layout-inline-end)',
      },
      infoIconOffset: '-4px',
      inputSectionVerticalGap: 'var(--input-simulator-vertical-gap)',
    },
  };
}

const BreakpointsWrapper = styled.div`
  --header-layout-inline-start: ${(props) => props.theme.size.grid002};
  --header-layout-inline-end: ${(props) => props.theme.size.grid002};
  --faq-layout-inline-start: ${(props) => props.theme.size.grid001};
  --faq-layout-inline-end: ${(props) => props.theme.size.grid001};
  --carselection-inline-start: ${(props) => props.theme.size.grid002};
  --carselection-inline-end: ${(props) => props.theme.size.grid002};
  --input-inline-start: ${(props) => props.theme.size.grid002};
  --input-inline-end: ${(props) => props.theme.size.grid002};
  --output-range-inline-start: ${(props) => props.theme.size.grid001};
  --output-range-inline-end: ${(props) => props.theme.size.grid001};
  --output-charge-cost-inline-start: ${(props) => props.theme.size.grid001};
  --output-charge-cost-inline-end: ${(props) => props.theme.size.grid001};
  --input-simulator-vertical-gap: ${(props) => props.theme.size.dynamic0150};

  @media (min-width: ${VwBreakpoints.b560}px) {
    --input-simulator-vertical-gap: ${(props) => props.theme.size.dynamic0100};
  }

  @media (min-width: ${Breakpoints.twoColumnLayout}px) {
    --header-layout-inline-start: ${(props) => props.theme.size.grid001};
    --header-layout-inline-end: ${(props) => props.theme.size.grid001};
    --faq-layout-inline-start: ${(props) => props.theme.size.grid001};
    --faq-layout-inline-end: ${(props) => props.theme.size.grid001};
    --carselection-inline-start: '0';
    --carselection-inline-end: '0';
    --input-inline-start: ${(props) => props.theme.size.grid001};
    --input-inline-end: ${(props) => props.theme.size.grid001};
    --output-range-inline-start: '0';
    --output-range-inline-end: '0';
    --output-charge-cost-inline-start: '0';
    --output-charge-cost-inline-end: '0';
    --two-column-simulator-content-layout-inline-start: ${(props) =>
      props.theme.size.grid001};
    --two-column-simulator-content-layout-inline-end: ${(props) =>
      props.theme.size.grid001};
  }

  @media (min-width: ${VwBreakpoints.b1280}px) {
    --header-layout-inline-start: ${(props) => props.theme.size.grid003};
    --header-layout-inline-end: ${(props) => props.theme.size.grid003};
    --faq-layout-inline-start: ${(props) => props.theme.size.grid003};
    --faq-layout-inline-end: ${(props) => props.theme.size.grid003};
    --two-column-simulator-content-layout-inline-start: ${(props) =>
      props.theme.size.grid003};
    --two-column-simulator-content-layout-inline-end: ${(props) =>
      props.theme.size.grid003};
  }
`;
